import {
  Component,
  OnInit,
  OnDestroy,
  effect,
  inject,
  signal,
  computed,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppVersionService } from '../../services/app-version.service';
import { AuthService } from '../../services/auth.service';
import { AdminConsoleService } from '../../services/admin-console.service';
import { ToastFeedbackService } from '../../services/toast-feedback.service';
import { environment } from '../../../environments/environment';
import { PathwaysLogoComponent } from '../pathways-logo/pathways-logo.component'; // PAT-123

interface EndMaintenanceBody {
  version: string;
  releaseNotes: string;
}

// PAT-203 — full-screen maintenance lock + admin end-maintenance release form.
@Component({
  selector: 'app-maintenance-overlay',
  standalone: true,
  imports: [FormsModule, PathwaysLogoComponent],
  templateUrl: './maintenance-overlay.component.html',
  styleUrl: './maintenance-overlay.component.scss',
})
export class MaintenanceOverlayComponent implements OnInit, OnDestroy {
  readonly versionSvc = inject(AppVersionService);
  readonly auth = inject(AuthService);
  private readonly adminConsole = inject(AdminConsoleService);
  readonly canManageMaintenance = this.adminConsole.canManageMaintenance;
  readonly maintenanceActive = this.versionSvc.maintenanceActive;
  private readonly toast = inject(ToastFeedbackService);
  private readonly apiBase = environment.chatApiBaseUrl.replace(/\/api\/chat.*$/, '');

  readonly releaseVersion = signal<string>('');
  readonly releaseNotes = signal<string>('');
  readonly previewHtml = computed(() => this.parseMarkdown(this.releaseNotes()));
  readonly submitting = signal<boolean>(false);

  readonly canSubmit = computed(
    () =>
      !this.submitting() &&
      this.releaseVersion().trim().length > 0 &&
      this.releaseNotes().trim().length > 0,
  );

  constructor() {
    // PAT-636 — load the grant when an admin session is active; hide controls otherwise.
    effect(() => {
      if (this.auth.isAdmin()) {
        void this.adminConsole.loadMaintenanceAccess();
      } else {
        this.adminConsole.clearMaintenanceAccess();
      }
    });
  }

  ngOnInit(): void {
    this.versionSvc.startStatusPoll();
  }

  ngOnDestroy(): void {
    this.versionSvc.stopStatusPoll();
  }

  onVersionInput(event: Event): void {
    this.releaseVersion.set((event.target as HTMLInputElement).value);
  }

  onNotesInput(event: Event): void {
    this.releaseNotes.set((event.target as HTMLTextAreaElement).value);
  }

  async endMaintenance(): Promise<void> {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    try {
      const body: EndMaintenanceBody = {
        version: this.releaseVersion().trim(),
        releaseNotes: this.releaseNotes().trim(),
      };
      const res = await fetch(`${this.apiBase}/api/admin/end-maintenance`, {
        method: 'POST',
        headers: this.auth.mergeAuthHeaders({
          'Content-Type': 'application/json',
          ...this.auth.getXUserHeader(),
        }),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(await this.readApiError(res));
      }
      const payload = (await res.json().catch(() => ({}))) as { crossPodBroadcast?: boolean };
      if (payload.crossPodBroadcast === false) {
        await this.toast.show(
          'Maintenance ended on this server. Other tabs/pods will unlock via status poll.',
          { variant: 'info' },
        );
      }
      // Trigger locally — SSE broadcast covers all other pods when Redis pub/sub is healthy
      this.versionSvc.onMaintenanceEnd();
    } catch (e: unknown) {
      await this.toast.show(
        'Failed to end maintenance: ' + (e instanceof Error ? e.message : String(e)),
        { variant: 'error' },
      );
      this.submitting.set(false);
    }
  }

  /** PAT-301 — prefer API detail over bare status codes. */
  private async readApiError(res: Response): Promise<string> {
    try {
      const err = (await res.json()) as { detail?: unknown };
      if (typeof err.detail === 'string' && err.detail.trim()) return err.detail.trim();
    } catch {
      // non-JSON
    }
    return `HTTP ${res.status}`;
  }

  private parseMarkdown(md: string): string {
    if (!md.trim()) return '';
    let html = md
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
      .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
    // Wrap consecutive <li> items into <ul> BEFORE newline substitution
    html = html.replace(/((<li>[^\n]*<\/li>\n?)+)/g, m => '<ul>' + m.replace(/\n/g, '') + '</ul>');
    html = html.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>');
    return `<p>${html}</p>`;
  }
}
